/**
 * Telling families, or not, when staff change their lessons.
 *
 * One place, because the decision is the same decision in three screens and the
 * *wording* is a thing that will change once somebody has counted actual
 * emails. Three copies of a sentence is three chances to update two of them.
 *
 * **The default differs by situation, not by endpoint**, and that distinction is
 * load-bearing. One lesson approved at a desk is somebody waiting to hear;
 * thirty lessons cancelled in one action is thirty emails nobody asked for. A
 * screen that reads its default off the operation it happens to call is how a
 * batch sends thirty notifications while every individual setting looks right.
 */

/**
 * What a bulk notification actually costs, said before the button.
 *
 * **Provisional wording, deliberately the pessimistic one.** An Amelia
 * appointment can hold several bookings — a group lesson, a waiting-list entry
 * — and Amelia's documentation does not settle whether a mutation sends one
 * notification per appointment or one per customer on it. Scenario 12 of
 * `docs/NOTIFICATION-ACCEPTANCE.md` measures it with two customers on two
 * distinct addresses.
 *
 * Until then this says the higher number, because the two errors are not equal.
 * Overstating costs somebody a moment's caution. Understating means they send
 * more emails than they agreed to and learn the real number from the replies.
 */
export const BULK_NOTIFY_WARNING =
  "This sends one notification per affected booking, so a lesson shared by two people may send two.";

/**
 * The control itself.
 *
 * A checkbox rather than a pair of buttons: this is a modifier on an action
 * somebody has already chosen, not a choice between two actions, and the label
 * says what happens when it is ticked rather than naming a setting.
 */
export function NotifyToggle({
  checked,
  onChange,
  label,
  warn = false,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  /** Show the cost. Only worth saying where the count can be more than one. */
  warn?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="stack" style={{ gap: 4 }}>
      <label className="row" style={{ gap: 8, alignItems: "start", flexWrap: "nowrap" }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span style={{ flex: 1 }}>{label}</span>
      </label>
      {/* Only when it is switched on. A warning about something not happening
          is noise, and noise is what people learn to read past. */}
      {warn && checked && <p className="small muted" style={{ margin: 0 }}>{BULK_NOTIFY_WARNING}</p>}
    </div>
  );
}
